// Merging from the record's Pull request tab (milestone 2): the Merge button, disabled with the
// API guard's first failing reason, and the confirm dialog. The guard and the merge are the API's
// (GET …/merge-guard, POST …/merge); the client decides nothing beyond "viewers cannot merge".
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  api,
  errorMessage,
  linkGitHubHref,
  type GitHubLink,
  type MergeGuard,
  type MergeResult,
  type PullSummary,
  type Role,
} from '../api/client';
import { RunMark } from './Bits';
import { Icon } from './Icon';
import { Button, buttonClass, Callout, cn, Dialog, DialogBody, DialogFooter, DialogTitle, mono } from './ui';

type Method = MergeGuard['method'];

const METHOD_NAME: Record<Method, string> = { merge: 'Merge commit', squash: 'Squash', rebase: 'Rebase' };
const METHOD_ACTION: Record<Method, string> = {
  merge: 'Create a merge commit',
  squash: 'Squash and merge',
  rebase: 'Rebase and merge',
};

type GuardState =
  | { state: 'loading' }
  | { state: 'ok'; guard: MergeGuard }
  | { state: 'error'; message: string };

const here = (): string => `${window.location.pathname}${window.location.search}`;

function MergeDialog({
  guard,
  busy,
  onCancel,
  onConfirm,
}: {
  guard: MergeGuard;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancel = useRef<HTMLButtonElement>(null);
  const c = guard.checks;
  const sha = (guard.head_sha ?? '').slice(0, 7);
  const fact = (dt: string, dd: ReactNode, monoDd = true, title?: string) => (
    <div>
      <dt className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted">{dt}</dt>
      <dd className={cn('m-0 mt-0.5 text-ink-display [overflow-wrap:anywhere]', monoDd && mono)} title={title}>
        {dd}
      </dd>
    </div>
  );
  return (
    <Dialog onClose={onCancel} busy={busy} labelledBy="merge-h" describedBy="merge-title" initialFocus={cancel}>
      <DialogBody>
        <DialogTitle id="merge-h">
          Merge <span className={mono}>#{guard.number}</span> into <span className={mono}>{guard.base}</span>?
        </DialogTitle>
        <p id="merge-title" className="-mt-2 mb-0 text-[13px] [overflow-wrap:anywhere]">
          {guard.title}
        </p>
        <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-x-3.5 gap-y-2 text-[12.5px]">
          {fact('Repo', guard.repo)}
          {fact('Base', guard.base)}
          {fact('Method', METHOD_NAME[guard.method], false)}
          {fact('Head', sha, true, guard.head_sha ?? undefined)}
          {fact('As', `@${guard.login}`)}
        </dl>
        <div
          className="flex flex-wrap gap-x-3.5 gap-y-2 rounded border border-hairline px-3 py-[9px] text-[12px] [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1.5"
          role="group"
          aria-label="Checks summary"
        >
          {c.total === 0 ? (
            <span>
              <Icon name="warn" small />
              No checks ran on this commit
            </span>
          ) : (
            <>
              <span className="font-semibold text-ink-display">
                {c.pass} of {c.total} {c.total === 1 ? 'check' : 'checks'} passed
              </span>
              {c.runs.map((r, i) => (
                <span key={`${r.kind}-${r.name}-${i}`}>
                  <RunMark state={r.state} />
                  {r.name}
                </span>
              ))}
            </>
          )}
        </div>
        <Callout tone="warning" icon={<Icon name="warn" small />} className="text-[12px]">
          The card will show <b>Merged — needs report</b> until the report is filed in the meta repo. The branch{' '}
          <span className={mono}>{guard.head}</span> is kept.
        </Callout>
      </DialogBody>
      <DialogFooter>
        <Button ref={cancel} onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={busy}>
          <Icon name="merge" />
          {busy ? 'Merging…' : METHOD_ACTION[guard.method]}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export function MergeArea({
  repo,
  pr,
  role,
  github,
  onMerged,
}: {
  repo: number;
  pr: PullSummary;
  role: Role;
  github: GitHubLink;
  onMerged: (result: MergeResult) => void;
}) {
  const owner = role === 'owner';
  const key = pr.repo_key;
  const [g, setG] = useState<GuardState>({ state: 'loading' });
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const reasonId = useId();

  useEffect(() => {
    if (!owner || !key) return;
    let live = true;
    setG({ state: 'loading' });
    api
      .GET('/api/repos/{repo_id}/pulls/{code_repo}/{number}/merge-guard', {
        params: { path: { repo_id: repo, code_repo: key, number: pr.number } },
      })
      .then(({ data, error, response }) => {
        if (!live) return;
        setG(data ? { state: 'ok', guard: data } : { state: 'error', message: errorMessage(error, response.status) });
      })
      .catch(() => live && setG({ state: 'error', message: errorMessage(null) }));
    return () => {
      live = false;
    };
  }, [owner, repo, key, pr.number, pr.head_sha, github.linked, tick]);

  let reason: string | null = null;
  const guard = g.state === 'ok' ? g.guard : null;
  if (!owner) reason = 'Only a workspace owner can merge.';
  else if (!key) reason = "This PR's repo is not in the meta repo's repos.yaml.";
  else if (g.state === 'loading') reason = 'Checking whether it can merge…';
  else if (g.state === 'error') reason = g.message;
  else if (guard && !guard.allowed) {
    reason =
      guard.code === 'not_linked' && !github.available
        ? 'Merging is not set up on this board (GitHub linking is off).'
        : guard.reason;
  }
  const allowed = owner && guard?.allowed === true;

  async function confirm() {
    if (!guard?.head_sha || !key) return;
    setBusy(true);
    try {
      const { data, error, response } = await api.POST('/api/repos/{repo_id}/pulls/{code_repo}/{number}/merge', {
        params: { path: { repo_id: repo, code_repo: key, number: pr.number } },
        body: { head_sha: guard.head_sha },
      });
      setOpen(false);
      if (data) {
        setFailed(null);
        onMerged(data);
        return;
      }
      const message = errorMessage(error, response.status);
      setFailed(message.startsWith('merge failed:') ? message : `merge failed: ${message}`);
      setTick((t) => t + 1); // ask the guard again: something changed
    } catch {
      setOpen(false);
      setFailed(`merge failed: ${errorMessage(null)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-2.5 border-t border-hairline px-3.5 py-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2.5">
        <button
          type="button"
          className={buttonClass('primary')}
          disabled={!allowed}
          aria-describedby={reasonId}
          aria-busy={owner && g.state === 'loading' ? true : undefined}
          onClick={() => setOpen(true)}
        >
          <Icon name="merge" />
          Merge
        </button>
        {allowed && guard ? (
          <span className="min-w-0 text-[12px] text-ink-muted [overflow-wrap:anywhere]" id={reasonId}>
            {METHOD_NAME[guard.method].toLowerCase()} into <span className={mono}>{guard.base}</span> as{' '}
            <span className={mono}>@{guard.login}</span>
          </span>
        ) : (
          <span
            className="inline-flex min-w-0 items-center gap-1.5 text-[12px] text-ink-body [overflow-wrap:anywhere] [&>svg]:text-checks-pending"
            id={reasonId}
            data-code={guard?.code ?? undefined}
          >
            {g.state !== 'loading' || !owner ? <Icon name="warn" small /> : null}
            {reason}
          </span>
        )}
        {owner && guard?.code === 'not_linked' && github.available && (
          <a className={buttonClass()} target="_top" href={linkGitHubHref(here())}>
            <Icon name="gh" />
            Link GitHub
          </a>
        )}
      </div>
      {failed && (
        <Callout as="p" tone="danger" outline role="alert" className="gap-1.5 text-[12.5px] [overflow-wrap:anywhere]">
          <Icon name="x" small /> {failed}
        </Callout>
      )}
      {open && guard && (
        <MergeDialog guard={guard} busy={busy} onCancel={() => setOpen(false)} onConfirm={() => void confirm()} />
      )}
    </div>
  );
}

/** The top bar's GitHub account control: link, or the linked login with a two-step unlink. */
export function GitHubAccount({ github, onChange }: { github: GitHubLink; onChange: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (!github.available) return null;
  if (!github.linked) {
    return (
      <a
        className={buttonClass()}
        target="_top" href={linkGitHubHref(here())}
        aria-label="Link GitHub"
        title="Link your GitHub account to merge PRs as yourself"
      >
        <Icon name="gh" />
        <span className="hide-phone phone:hidden">Link GitHub</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      className={buttonClass(confirming ? 'danger' : 'secondary')}
      aria-label={confirming ? `Confirm: unlink GitHub account @${github.login}` : `GitHub: @${github.login}. Unlink`}
      title={confirming ? 'Click again to unlink' : `Linked to GitHub as @${github.login}. Click to unlink`}
      onBlur={() => setConfirming(false)}
      onClick={async () => {
        if (!confirming) {
          setConfirming(true);
          return;
        }
        await api.DELETE('/api/me/github');
        setConfirming(false);
        onChange();
      }}
    >
      <Icon name="gh" />
      <span className="hide-phone phone:hidden">{confirming ? 'Unlink?' : `@${github.login}`}</span>
    </button>
  );
}
